import React from 'react';
import { Link } from 'react-router-dom';
import { variantLabel } from '../../Parts/bomsApi';
import type { BuildCapacityData, DashboardCapacity } from '../types';
import Panel from '../../components/ui/Panel';
import EmptyState from '../../components/ui/EmptyState';
import { buttonClass } from '../../components/ui/Button';
import { CATEGORY } from '../../components/ui/categoryColors';

const Figure: React.FC<{ capacity: DashboardCapacity }> = ({ capacity }) => (
  <div className="min-w-0 max-w-[14rem]">
    <div className="text-[11px] text-on-surface-variant whitespace-nowrap">
      {variantLabel(capacity.variant)}
    </div>
    <div
      className={`text-3xl font-semibold tabular-nums leading-tight ${capacity.builds === 0 ? '' : 'text-on-surface'}`}
      style={capacity.builds === 0 ? { color: CATEGORY.red.ink.oklch } : undefined}
    >
      {capacity.builds}
    </div>
    {capacity.raw_builds !== capacity.builds && (
      <div className="text-[11px] text-on-surface-variant">of {capacity.raw_builds} on the shelf</div>
    )}
    {capacity.limited_by && (
      <div
        className="truncate text-[11px] text-on-surface-variant"
        title={`Limited by ${capacity.limited_by.name}`}
      >
        Limited by {capacity.limited_by.name}
      </div>
    )}
  </div>
);

const BuildCapacityModule: React.FC<{ buildCapacity?: Partial<BuildCapacityData> }> = ({
  buildCapacity,
}) => {
  const products = buildCapacity?.products ?? [];

  return (
    <Panel title="Build capacity">
      {products.length === 0 ? (
        <EmptyState
          variant="area"
          title="No parts lists on the dashboard yet"
          action={<Link to="/parts/boms" className={buttonClass('tonal', 'sm')}>Open Product BOMs</Link>}
        />
      ) : (
        <ul className="divide-y divide-outline-variant">
          {products.map((m) => (
            <li
              key={m.id}
              className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <div className="font-bold text-on-surface">{m.name}</div>
                {(m.model_code ?? m.sku) !== m.name && (
                  <div className="text-xs text-on-surface-variant">{m.model_code ?? m.sku}</div>
                )}
              </div>
              {m.capacity.length === 0 ? (
                <div>
                  <div className="text-3xl font-semibold tabular-nums leading-tight text-on-surface-variant">
                    0
                  </div>
                  <div className="text-[11px] text-on-surface-variant">No parts list yet</div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-x-8 gap-y-2">
                  {m.capacity.map((c) => (
                    <Figure key={c.variant ?? 'all'} capacity={c} />
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

export default BuildCapacityModule;
